function isBlockBoundary(node: Node): boolean {
  if (!(node instanceof HTMLElement)) return false;
  const tag = node.tagName;
  return (
    tag === 'BR' ||
    tag === 'DIV' ||
    tag === 'P' ||
    tag === 'LI' ||
    tag === 'UL' ||
    tag === 'OL' ||
    tag === 'BLOCKQUOTE'
  );
}

function isInsideList(node: Node, editor: HTMLElement): boolean {
  let n: Node | null = node;
  while (n && n !== editor) {
    if (n instanceof HTMLElement && (n.tagName === 'UL' || n.tagName === 'OL' || n.tagName === 'LI')) {
      return true;
    }
    n = n.parentNode;
  }
  return false;
}

function rangeFromLineStartToCaret(editor: HTMLElement, caret: Range): Range {
  const lineRange = caret.cloneRange();
  const container = caret.startContainer;

  if (container.nodeType === Node.TEXT_NODE) {
    const before = (container.textContent ?? '').slice(0, caret.startOffset);
    const nl = before.lastIndexOf('\n');
    if (nl >= 0) {
      lineRange.setStart(container, nl + 1);
      return lineRange;
    }

    let prev: Node | null = container.previousSibling;
    while (prev) {
      if (isBlockBoundary(prev)) {
        lineRange.setStartAfter(prev);
        return lineRange;
      }
      prev = prev.previousSibling;
    }

    const parent = container.parentNode;
    if (parent && parent !== editor && parent instanceof HTMLElement && isBlockBoundary(parent)) {
      lineRange.setStart(parent, 0);
      return lineRange;
    }

    lineRange.setStart(container, 0);
    return lineRange;
  }

  if (container instanceof HTMLElement) {
    lineRange.setStart(container, 0);
    return lineRange;
  }

  lineRange.setStart(editor, 0);
  return lineRange;
}

function getCurrentListItem(node: Node | null, editor: HTMLElement): HTMLLIElement | null {
  let n: Node | null = node;
  while (n && n !== editor) {
    if (n instanceof HTMLLIElement) return n;
    n = n.parentNode;
  }
  return null;
}

function listItemIsEmpty(li: HTMLLIElement): boolean {
  if (li.querySelector('img, video, iframe')) return false;
  return !(li.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

function caretIsAtStartOf(el: HTMLElement, sel: Selection): boolean {
  if (!sel.rangeCount || !sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  const pre = range.cloneRange();
  try {
    pre.selectNodeContents(el);
    pre.setEnd(range.startContainer, range.startOffset);
  } catch {
    return false;
  }
  return !pre.toString().replace(/\u00a0/g, ' ').trim();
}

function placeCaretIn(el: HTMLElement): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

function unwrapListItem(li: HTMLLIElement): HTMLElement {
  const list = li.parentElement;
  if (!list) return li;
  const parent = list.parentNode;
  if (!parent) return li;

  const block = document.createElement('div');
  while (li.firstChild) {
    const child = li.firstChild;
    const emptyBr =
      child instanceof HTMLBRElement &&
      !child.nextSibling &&
      !(li.textContent ?? '').replace(/\u00a0/g, ' ').trim();
    if (emptyBr) {
      li.removeChild(child);
      continue;
    }
    block.appendChild(child);
  }
  if (!block.firstChild) block.appendChild(document.createElement('br'));

  const restItems: Node[] = [];
  while (li.nextSibling) restItems.push(li.removeChild(li.nextSibling));
  li.remove();

  const restList =
    restItems.length > 0
      ? (() => {
          const nextList = list.cloneNode(false) as HTMLElement;
          for (const node of restItems) nextList.appendChild(node);
          return nextList;
        })()
      : null;

  if (list.childNodes.length === 0) {
    parent.replaceChild(block, list);
  } else {
    parent.insertBefore(block, list.nextSibling);
  }
  if (restList) parent.insertBefore(restList, block.nextSibling);
  return block;
}

function exitEmptyListItem(editor: HTMLElement, li: HTMLLIElement): boolean {
  editor.focus();
  const sel = window.getSelection();
  if (sel && document.execCommand('outdent')) {
    const still = sel.anchorNode ? getCurrentListItem(sel.anchorNode, editor) : null;
    if (!still || still !== li) return true;
  }
  placeCaretIn(unwrapListItem(li));
  return true;
}

export function matchMarkdownListPrefix(prefix: string): 'ul' | 'ol' | null {
  const normalized = prefix.replace(/\u00a0/g, ' ').trimEnd();
  if (normalized === '-' || normalized === '*') return 'ul';
  if (/^\d+[.)]$/.test(normalized)) return 'ol';
  return null;
}

/** Enter on an empty list item leaves the list (undoable via `outdent` when the browser supports it). */
export function tryExitEmptyListItem(editor: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel?.anchorNode || !editor.contains(sel.anchorNode)) return false;
  const li = getCurrentListItem(sel.anchorNode, editor);
  if (!li || !listItemIsEmpty(li)) return false;
  return exitEmptyListItem(editor, li);
}

/** Backspace at the start of an empty list item unwraps it. */
export function tryUnwrapListOnBackspace(editor: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel?.anchorNode || !editor.contains(sel.anchorNode) || !sel.isCollapsed) return false;
  const li = getCurrentListItem(sel.anchorNode, editor);
  if (!li || !listItemIsEmpty(li) || !caretIsAtStartOf(li, sel)) return false;
  return exitEmptyListItem(editor, li);
}

/** Converts a leading `-` / `*` into a `<ul>`, or `1.` / `1)` into an `<ol>`. Uses execCommand so Ctrl+Z undoes it. */
export function tryConvertMarkdownListAtCaret(editor: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return false;
  const anchor = sel.anchorNode;
  if (!anchor || !editor.contains(anchor)) return false;
  if (isInsideList(anchor, editor)) return false;

  const caret = sel.getRangeAt(0);
  const lineRange = rangeFromLineStartToCaret(editor, caret);
  const kind = matchMarkdownListPrefix(lineRange.toString());
  if (!kind) return false;

  editor.focus();
  sel.removeAllRanges();
  sel.addRange(lineRange);
  const html = kind === 'ol' ? '<ol><li><br></li></ol>' : '<ul><li><br></li></ul>';
  if (document.execCommand('insertHTML', false, html)) return true;

  lineRange.deleteContents();
  const list = document.createElement(kind);
  const li = document.createElement('li');
  li.appendChild(document.createElement('br'));
  list.appendChild(li);
  lineRange.insertNode(list);
  placeCaretIn(li);
  return true;
}

type ListKeyDownEvent = {
  key: string;
  nativeEvent: { isComposing?: boolean };
  preventDefault: () => void;
};

/** Space / Enter / Backspace list shortcuts used by notes and mail composers. */
export function handleContentEditableListKeyDown(
  e: ListKeyDownEvent,
  editor: HTMLElement | null,
): boolean {
  if (!editor || e.nativeEvent.isComposing) return false;
  if (e.key === ' ' && tryConvertMarkdownListAtCaret(editor)) {
    e.preventDefault();
    return true;
  }
  if (e.key === 'Enter' && tryExitEmptyListItem(editor)) {
    e.preventDefault();
    return true;
  }
  if (e.key === 'Backspace' && tryUnwrapListOnBackspace(editor)) {
    e.preventDefault();
    return true;
  }
  return false;
}
